<template>
  <div class="h-full w-full min-w-0 overflow-hidden flex flex-col justify-between bg-white border-solid border-l border-0 border-gray-border">
    <div class="min-w-0 overflow-y-auto overflow-x-hidden flex-1">
      <div class="border-solid border-b border-0 border-gray-border px-8 pb-8 min-w-0">
        <input-with-error-message v-if="isRenameMode"
                                  id="index-editor-name-input"
                                  :value.sync="indexSourceName"
                                  :is-loading="isLoadingName"
                                  @escape="$emit('set-rename-mode', false)"
                                  @submit="handleRename"
                                  class="h-full mt-5 w-full"
        />
        <div v-else class="mt-8 pr-12 min-w-0 relative">
          <h3 class="font-semibold flex items-center min-w-0 overflow-hidden">
            <span class="cursor-pointer text-gray-dark hover:text-primary-focus hover:underline min-w-0 truncate"
                  @click="$emit('display-index-editor')"
            >{{ index.name }}</span>
            <span class="text-gray mx-1 flex-shrink-0">/</span>
            <span class="text-black min-w-0 truncate">{{ indexSource.name }}</span>
          </h3>
          <side-list-menu v-if="!isRenameMode"
                          :items="menuItems"
                          class="absolute top-0 right-0 -mt-1"
                          @rename="$emit('set-rename-mode', true)"
                          @delete="$emit('delete-index-source', indexSource)"
          />
        </div>
      </div>

      <settings-indexes-file v-if="sourceType === KNOWLEDGE_BASES.FILE"
                             :file.sync="fileSettings.file"
                             :url.sync="fileSettings.url"
                             @reset:fileSettings="resetFileSettings"
                             class="mt-8 px-8"
      />

      <settings-indexes-web-page v-if="sourceType === KNOWLEDGE_BASES.WEB_PAGE"
                                 :url.sync="webPageSettings.url"
                                 :prompt.sync="webPageSettings.prompt"
                                 :format.sync="webPageSettings.format"
                                 :headerKey.sync="webPageSettings.headerKey"
                                 :headerValue.sync="webPageSettings.headerValue"
                                 class="mt-8"
                                 :container-classes="'px-8 pb-8'"
                                 :index-source="indexSource"
                                 @onAddHeaderChecked="updateWebPageSettingsHeader($event.headerKey, $event.headerValue)"
      />

      <settings-indexes-xml-feed v-if="sourceType === KNOWLEDGE_BASES.XML_FEED"
                                 :url.sync="xmlFeedSettings.url"
                                 :item.sync="xmlFeedSettings.item"
                                 :item-name.sync="xmlFeedSettings.itemName"
                                 :item-id.sync="xmlFeedSettings.itemId"
                                 :category="xmlFeedSettings.category"
                                 :field-selection="xmlFeedSettings.fieldSelection"
                                 :show-field-selection="showFieldSelection"
                                 :manual-config="xmlFeedSettings.manualConfig"
                                 :field-samples="fieldSamplesForCurrentUrl"
                                 @update:manual-config="xmlFeedSettings.manualConfig = $event"
                                 @update:field-selection="updateFieldSelection"
                                 @update:category-selection="updateCategorySelection"
                                 @update:available-tags="availableTags = $event"
                                 class="mt-8 px-8"
      />
    </div>

    <div class="grid grid-cols-2 gap-4 mb-10 px-8">
      <busy-button id="saveButton"
                   :variant="saveButtonVariant"
                   class="col-span-1"
                   :is-loading="isSaving"
                   :is-disabled="isSaving"
                   @click="saveIndex"
      >
        {{ saveButtonText }}
      </busy-button>

      <busy-button id="reindexButton"
                   variant="bordered"
                   class="col-span-1"
                   :is-loading="isLoadingReindex"
                   :is-disabled="isLoadingReindex"
                   @click="$emit('start-reindex', index)"
      >
        Reindex now
      </busy-button>
    </div>
  </div>
</template>

<script>
import BusyButton from '@/components/common/BusyButton'
import InputWithErrorMessage from '@/components/common/InputWithErrorMessage.vue'
import SideListMenu from '@/components/common/SideListMenu'
import toaster from '@/utils/toaster'
import { MENU_ITEMS, SAVE_BUTTON_VARIANTS } from '@/utils/editor.constants'
import { SAVE_BUTTON_LABELS } from '@/utils/text.constants'
import clone from 'ramda/src/clone'
import head from 'ramda/src/head'
import isEmpty from 'ramda/src/isEmpty'
import SettingsIndexesFile from '@/components/settings/indexes/SettingsIndexesFile.vue'
import SettingsIndexesWebPage from '@/components/settings/indexes/SettingsIndexesWebPage.vue'
import SettingsIndexesXmlFeed from '@/components/settings/indexes/SettingsIndexesXmlFeed.vue'
import { applyFieldSelection, extractCategoryTag, extractFieldSelection } from '@/utils/xml-source-config.utils'
import { KNOWLEDGE_BASES } from '@/utils/knowledgeBase.constants'
import errorUtils from '@/utils/error.utils'
import fileService from '@/services/amio/file.service'
import indexSourcesService from '@/services/amio/index-sources.service'
import previewModule from '@/store/modules/preview.module'

const UPDATE_BUTTON_LABEL = 'Save'
const SUCCESS_BUTTON_LABEL = 'Saved!'

export default {
  name: 'SettingsIndexSourceEditor',
  components: {
    SettingsIndexesFile,
    SettingsIndexesWebPage,
    SettingsIndexesXmlFeed,
    BusyButton,
    InputWithErrorMessage,
    SideListMenu
  },
  props: {
    index: {
      type: Object,
      required: true
    },
    indexSource: {
      type: Object,
      required: true
    },
    isLoadingName: {
      type: Boolean,
      default: false
    },
    isLoadingReindex: {
      type: Boolean,
      default: false
    },
    isSaving: {
      type: Boolean,
      default: false
    },
    isRenameMode: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    indexSource: {
      immediate: true,
      handler(indexSource, previous) {
        this.error = null
        if (this.sourceType === KNOWLEDGE_BASES.XML_FEED) {
          if (previous && previous.id === indexSource.id && this.isRenameMode) {
            this.indexSourceName = indexSource.name
            return
          }
          this.updateXMLFeedForm(indexSource)
        }
        if (this.sourceType === KNOWLEDGE_BASES.FILE) {
          this.updateFileForm(indexSource)
        }
        if (this.sourceType === KNOWLEDGE_BASES.WEB_PAGE) {
          this.updateWebPageForm(indexSource)
        }
      }
    }
  },
  data() {
    return {
      indexSourceName: '',
      webPageSettings: {
        url: '',
        prompt: null,
        format: 'txt',
        headerKey: null,
        headerValue: null
      },
      xmlFeedSettings: {
        manualConfig: false,
        url: '',
        item: '',
        itemName: '',
        itemId: '',
        category: '',
        fieldSelection: { embed_fields: [], payload_fields: {}, group_key: [] }
      },
      availableTags: [],
      fileSettings: {
        file: { name: '' }
      },
      saveButtonVariant: SAVE_BUTTON_VARIANTS.DEFAULT,
      saveButtonText: UPDATE_BUTTON_LABEL,
      isEmpty,
      KNOWLEDGE_BASES
    }
  },
  computed: {
    error: previewModule.error,
    errorDescription: previewModule.errorDescription,

    menuItems() {
      const menuItemReindex = { ...MENU_ITEMS.reindexAllSources, label: 'Reindex' }
      if (this.index.sources.length === 1) return [MENU_ITEMS.rename, menuItemReindex]

      const menuItemDelete = { ...MENU_ITEMS.delete, label: 'Delete source' }
      return [MENU_ITEMS.rename, menuItemReindex, menuItemDelete]
    },
    sourceType() {
      switch (this.indexSource.type) {
        case 'file':
          return KNOWLEDGE_BASES.FILE
        case 'url':
          return KNOWLEDGE_BASES.XML_FEED
        case 'web_scraping':
          return KNOWLEDGE_BASES.WEB_PAGE
        default:
          return null
      }
    },
    // The samples describe the feed at the STORED url. The moment the url field is edited they are
    // about a different feed, so they stop being offered and the form falls back to its pending state
    // — otherwise the editor invites you to pick tags from the feed you are replacing. (Saving such a
    // mapping is already refused engine-side, which clears the config on a url change, but the editor
    // should not have offered it in the first place.)
    fieldSamplesForCurrentUrl() {
      if (this.xmlFeedSettings.url !== this.indexSource.url) return null
      return this.indexSource.field_samples
    },

    // Qdrant XML sources can configure embed/payload/group-key per field, shown once the indexer's
    // field samples have surfaced the available tags (i.e. after the first indexation).
    showFieldSelection() {
      return this.index.provider === 'qdrant' &&
        this.sourceType === KNOWLEDGE_BASES.XML_FEED &&
        this.availableTags.length > 0
    }
  },
  methods: {
    updateWebPageSettingsHeader(headerKey, headerValue) {
      this.webPageSettings.headerKey = headerKey
      this.webPageSettings.headerValue = headerValue
    },
    updateWebPageForm(indexSource) {
      this.indexSourceName = indexSource.name
      const { url, config } = indexSource
      this.webPageSettings.url = url
      if (config?.prompt) this.webPageSettings.prompt = config.prompt
      if (config?.format) this.webPageSettings.format = config.format
      if (config?.header?.key) this.webPageSettings.headerKey = config.header.key
      if (config?.header?.value) this.webPageSettings.headerValue = config.header.value
      if (config?.error) this.error = { description: config.error }
    },

    updateXMLFeedForm(indexSource) {
      this.indexSourceName = indexSource.name
      const { url, config } = indexSource
      const tagMapping = config?.tag_mapping || {}
      this.xmlFeedSettings = {
        url,
        item: tagMapping.item || '',
        itemName: tagMapping.item_name || '',
        itemId: tagMapping.item_id || '',
        category: extractCategoryTag(config),
        // Whether a HUMAN owns this configuration — a stored fact, not a guess from the config.
        //
        // This used to read `!!tagMapping.item`, which was fine while only hand-filled sources had a
        // config. Since the indexer derives one for every feed it means "has a config", so unticking
        // the box did nothing and it re-ticked itself on the next reload. Ticked now means exactly
        // what the label says: the user's selection is the truth.
        manualConfig: !!indexSource.config_manual,
        // load the saved Qdrant field selection so the editor shows the current embed/payload/group_key
        fieldSelection: extractFieldSelection(config)
      }
    },

    updateFieldSelection(fieldSelection) {
      this.xmlFeedSettings.fieldSelection = fieldSelection
    },

    updateCategorySelection({ category, fieldSelection }) {
      this.xmlFeedSettings.category = category
      this.xmlFeedSettings.fieldSelection = fieldSelection
    },

    updateFileForm(indexSource) {
      this.indexSourceName = indexSource.name
      this.fileSettings.file = {
        name: this.getFileName(indexSource.url),
        url: indexSource.url,
        init: true
      }
    },

    getFileName(url) {
      const indexOfFile = url.indexOf('_FILE_')
      return indexOfFile !== -1 ?
        url.substring(indexOfFile + '_FILE_'.length) :
        url.split('/').pop()
    },

    updateScheduleParams(scheduleParams) {
      this.scheduleParams = scheduleParams
    },

    handleRename() {
      const updatedIndexSource = clone(this.indexSource)
      updatedIndexSource.name = this.indexSourceName
      this.$emit('rename', updatedIndexSource)
    },

    saveIndex() {
      this.error = null
      if (this.sourceType === KNOWLEDGE_BASES.XML_FEED) {
        this.saveXMLIndex()
      }
      if (this.sourceType === KNOWLEDGE_BASES.FILE) {
        this.saveFileIndex()
      }
      if (this.sourceType === KNOWLEDGE_BASES.WEB_PAGE) {
        this.saveWebPageIndex()
      }
    },

    async saveWebPageIndex() {
      try {
        const config = {
          prompt: this.webPageSettings.prompt,
          format: this.webPageSettings.format
        }
        if (this.webPageSettings.headerKey !== null || this.webPageSettings.headerValue !== null) {
          config.header = {
            key: this.webPageSettings.headerKey,
            value: this.webPageSettings.headerValue
          }
        }
        if (this.indexSource.config?.content) config.content = this.indexSource.config.content
        const patchData = {
          type: 'web_scraping',
          format: 'html',
          url: this.webPageSettings.url,
          name: this.indexSourceName,
          config: config
        }
        await indexSourcesService.update(this.index.id, this.indexSource.id, patchData)
        this.error = null
        this.$emit('reload-indexes')
      } catch (err) {
        this.handleError(err)
      } finally {
        this.startButtonStateTimeout()
      }
    },

    async saveXMLIndex() {
      try {
        const { url, item, itemName, itemId, category, fieldSelection, manualConfig } = this.xmlFeedSettings
        const patchData = {
          type: 'url',
          format: 'xml',
          url: url,
          name: this.indexSourceName
        }
        // Only send `config` when the user is actually configuring fields. Sending it unconditionally
        // meant saving a rename on a source whose config the INDEXER derived overwrote that config with
        // an empty tag_mapping (PRO-658) — the form has nothing to send before the first indexation.
        // The engine leaves a stored config untouched when the key is absent.
        // Unticking the toggle hands the fields back to the indexer, so the stored config has to go —
        // an explicit null is how the engine is told to drop it (absent means "leave it alone", which
        // is what a rename needs). Without this the untick was a no-op.
        if (!manualConfig && this.indexSource.config_manual) {
          patchData.config = null
          patchData.config_manual = false
        }
        // A URL edit means the form's mapping describes the feed being replaced, so it must not be
        // sent: the engine honours a config supplied alongside a new URL (an API caller sending both
        // knows the new feed's mapping), which makes it this client's job to know its own form is
        // stale. Omitting it lets the indexer derive from the new feed.
        if (manualConfig && url === this.indexSource.url) {
          // The form owns every key the engine's (closed) xml config schema accepts, so it rebuilds
          // the config wholesale — PRO-661 removed `vectorization_skip_tags`, the last key it did not
          // own and had to carry forward across a save.
          //
          // Category is stored only as a payload field, never on tag_mapping.
          patchData.config = applyFieldSelection({
            tag_mapping: { item, item_name: itemName, item_id: itemId }
          }, fieldSelection, category)
          // Ownership is stored, not inferred from the config. The engine sets the flag from whether
          // this request supplies a config (it ignores a client-sent config_manual); GET always
          // returns the boolean for URL sources. Sending config is what keeps the tick after reload.
          patchData.config_manual = true
        }
        await indexSourcesService.update(this.index.id, this.indexSource.id, patchData)
        this.error = null
        this.$emit('reload-indexes')
      } catch (err) {
        this.handleError(err)
      } finally {
        this.startButtonStateTimeout()
      }
    },

    async saveFileIndex() {
      try {
        const patchData = { type: 'file', name: this.indexSourceName }
        if (this.fileSettings.file instanceof File) {
          const response = await fileService.create(this.fileSettings.file)
          patchData.format = response.format
          patchData.url = response.url
        } else if (this.fileSettings.url) {
          patchData.format = this.fileSettings.url.split('.').pop()
          patchData.url = this.fileSettings.url
        }

        await indexSourcesService.update(this.index.id, this.indexSource.id, patchData)
        this.error = null
        this.$emit('reload-indexes')
      } catch (err) {
        this.handleError(err)
      } finally {
        this.startButtonStateTimeout()
      }
    },

    startButtonStateTimeout() {
      this.setButtonProps(this.error ? 'ERROR' : 'SUCCESS')
      this.buttonStateTimeout = setTimeout(() => {
        this.setButtonProps()
      }, 3000)
    },

    setButtonProps(buttonState = 'DEFAULT') {
      this.saveButtonVariant = SAVE_BUTTON_VARIANTS[buttonState]
      if (buttonState === 'DEFAULT') {
        this.saveButtonText = UPDATE_BUTTON_LABEL
      } else if (buttonState === 'SUCCESS') {
        this.saveButtonText = SUCCESS_BUTTON_LABEL
      } else {
        this.saveButtonText = SAVE_BUTTON_LABELS[buttonState]
      }
    },

    resetFileSettings() {
      this.fileSettings = { file: null, url: '' }
    },

    handleError(err) {
      if (err.message === 'Payload Too Large') {
        this.error = { description: 'File exceeds 1 MB limit.' }
        return
      }

      const errorMessage = head(err.response.data.details.errors)?.message || err.message
      if (errorUtils.isIndexCreateUpdateError(errorMessage)) {
        this.error = { description: errorMessage }
        return
      }

      this.error = errorMessage
      toaster.error(errorMessage)
    }
  }
}
</script>
